import React, { useState, useEffect } from 'react';
import { ArrowLeft, Play, RotateCcw, Trophy, Dice1, Dice2, Dice3, Dice4, Dice5, Dice6 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';

interface ProbabilityRaceGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface Racer {
  id: number;
  name: string;
  color: string;
  position: number;
  diceRule: string;
  probability: number;
  wins: number;
}

interface RaceResult {
  winner: number;
  positions: number[];
}

const ProbabilityRaceGame: React.FC<ProbabilityRaceGameProps> = ({ mode, onReset, language }) => {
  const [racers, setRacers] = useState<Racer[]>([]);
  const [isRacing, setIsRacing] = useState(false);
  const [raceHistory, setRaceHistory] = useState<RaceResult[]>([]);
  const [currentDice, setCurrentDice] = useState<number[]>([]);
  const [userPrediction, setUserPrediction] = useState<number | null>(null);
  const [showPredictionResult, setShowPredictionResult] = useState(false);
  const [predictionCorrect, setPredictionCorrect] = useState(false);
  const [raceCount, setRaceCount] = useState(0);
  const [score, setScore] = useState(0);
  const [gamePhase, setGamePhase] = useState<'prediction' | 'racing' | 'results'>('prediction');
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const [playSuccess] = useSound(successSound, { volume: 0.5 });

  const raceTrackLength = 20;

  const initializeRacers = () => {
    const racerData: Racer[] = [
      {
        id: 1,
        name: language === 'en' ? 'Lightning' : 'Éclair',
        color: 'bg-red-500',
        position: 0,
        diceRule: language === 'en' ? 'Moves on 5 or 6' : 'Avance sur 5 ou 6',
        probability: 2/6,
        wins: 0
      },
      {
        id: 2,
        name: language === 'en' ? 'Thunder' : 'Tonnerre',
        color: 'bg-blue-500',
        position: 0,
        diceRule: language === 'en' ? 'Moves on even numbers' : 'Avance sur nombres pairs',
        probability: 3/6,
        wins: 0
      },
      {
        id: 3,
        name: language === 'en' ? 'Storm' : 'Tempête',
        color: 'bg-green-500',
        position: 0,
        diceRule: language === 'en' ? 'Moves on 1, 2, or 3' : 'Avance sur 1, 2 ou 3',
        probability: 3/6,
        wins: 0
      },
      {
        id: 4,
        name: language === 'en' ? 'Flash' : 'Flash',
        color: 'bg-yellow-500',
        position: 0,
        diceRule: language === 'en' ? 'Moves on any number' : 'Avance sur tout nombre',
        probability: 6/6,
        wins: 0
      }
    ];
    setRacers(racerData);
  };

  useEffect(() => {
    initializeRacers();
  }, [language]);

  const rollDice = () => {
    return Math.floor(Math.random() * 6) + 1;
  };

  const getDiceIcon = (value: number) => {
    const icons = [Dice1, Dice2, Dice3, Dice4, Dice5, Dice6];
    const Icon = icons[value - 1];
    return <Icon className="h-6 w-6" />;
  };

  const checkMovement = (racerId: number, diceValue: number): boolean => {
    switch (racerId) {
      case 1: return diceValue >= 5; // 5 ou 6
      case 2: return diceValue % 2 === 0; // nombres pairs
      case 3: return diceValue <= 3; // 1, 2 ou 3
      case 4: return true; // tout nombre
      default: return false;
    }
  };

  const runRace = async () => {
    setIsRacing(true);
    setGamePhase('racing');
    
    // Reset positions
    setRacers(prev => prev.map(racer => ({ ...racer, position: 0 })));
    
    const raceSteps: number[][] = [];
    let winner: number | null = null;
    
    while (winner === null) {
      const dice = Array.from({ length: 4 }, () => rollDice());
      raceSteps.push(dice);
      setCurrentDice(dice);
      
      // Update positions
      setRacers(prev => {
        const updated = prev.map((racer, index) => {
          const canMove = checkMovement(racer.id, dice[index]);
          const newPosition = canMove ? racer.position + 1 : racer.position;
          
          if (newPosition >= raceTrackLength && winner === null) {
            winner = racer.id;
          }
          
          return { ...racer, position: newPosition };
        });
        return updated;
      });
      
      await new Promise(resolve => setTimeout(resolve, 800));
    }
    
    // Update winner stats
    setRacers(prev => prev.map(racer => 
      racer.id === winner 
        ? { ...racer, wins: racer.wins + 1 }
        : racer
    ));
    
    // Check prediction
    const correct = userPrediction === winner;
    setPredictionCorrect(correct);
    setShowPredictionResult(true);
    
    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);
    
    if (correct) {
      setScore(score + 1);
      playSuccess();
    }
    
    setRaceHistory(prev => [...prev, {
      winner: winner!,
      positions: racers.map(r => r.position)
    }]);
    
    setRaceCount(prev => prev + 1);
    setIsRacing(false);
    setGamePhase('results');
    
    if (raceCount >= 9) {
      saveProgress(newStats);
    }
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'probability',
        mode: 'race',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    initializeRacers();
    setRaceHistory([]);
    setRaceCount(0);
    setScore(0);
    setUserPrediction(null);
    setShowPredictionResult(false);
    setGamePhase('prediction');
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
  };

  const nextRace = () => {
    setUserPrediction(null);
    setShowPredictionResult(false);
    setGamePhase('prediction');
    setCurrentDice([]);
  };

  return (
    <div className="w-full max-w-6xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <div className="text-lg font-medium text-indigo-700 dark:text-indigo-300">
            {language === 'en' ? `Race ${raceCount + 1}/10` : `Course ${raceCount + 1}/10`}
          </div>
          <button
            onClick={resetGame}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            <RotateCcw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {raceCount >= 10 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Trophy className="h-16 w-16 text-indigo-600 dark:text-indigo-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Race Series Complete!' : 'Série de courses terminée !'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{score}/10</div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Correct Predictions' : 'Prédictions correctes'}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                {Math.round((score / 10) * 100)}%
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Accuracy' : 'Précision'}
              </div>
            </div>
          </div>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600"
          >
            {language === 'en' ? 'Play Again' : 'Rejouer'}
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Informations des coureurs */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-6">
              {language === 'en' ? 'Racers & Rules' : 'Coureurs et Règles'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {racers.map((racer) => (
                <div key={racer.id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-4 h-4 rounded-full ${racer.color}`}></div>
                    <span className="font-medium text-slate-800 dark:text-slate-100">
                      {racer.name}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    {racer.diceRule}
                  </p>
                  <div className="text-xs text-slate-500 dark:text-slate-500">
                    {language === 'en' ? 'Probability:' : 'Probabilité :'} {Math.round(racer.probability * 100)}%
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-500">
                    {language === 'en' ? 'Wins:' : 'Victoires :'} {racer.wins}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prédiction */}
          {gamePhase === 'prediction' && (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                {language === 'en' ? 'Make Your Prediction' : 'Faites votre prédiction'}
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                {language === 'en' 
                  ? 'Which racer do you think will win? Consider their movement rules and probabilities.'
                  : 'Quel coureur pensez-vous qui va gagner ? Considérez leurs règles de mouvement et probabilités.'}
              </p>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                {racers.map((racer) => (
                  <button
                    key={racer.id}
                    onClick={() => setUserPrediction(racer.id)}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      userPrediction === racer.id
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/50'
                        : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${racer.color}`}></div>
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                        {racer.name}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
              
              <button
                onClick={runRace}
                disabled={userPrediction === null}
                className="w-full px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Play className="h-5 w-5" />
                {language === 'en' ? 'Start Race!' : 'Commencer la course !'}
              </button>
            </div>
          )}

          {/* Piste de course */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {language === 'en' ? 'Race Track' : 'Piste de course'}
            </h3>
            
            {/* Dés actuels */}
            {currentDice.length > 0 && (
              <div className="mb-4 flex justify-center gap-4">
                {currentDice.map((dice, index) => (
                  <div key={index} className="flex flex-col items-center gap-1">
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {racers[index]?.name}
                    </div>
                    <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded">
                      {getDiceIcon(dice)}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <div className="space-y-4">
              {racers.map((racer) => (
                <div key={racer.id} className="relative">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-3 h-3 rounded-full ${racer.color}`}></div>
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {racer.name}
                    </span>
                  </div>
                  
                  {/* Piste */}
                  <div className="relative h-8 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                    {/* Ligne d'arrivée */}
                    <div className="absolute right-0 top-0 w-1 h-full bg-red-500"></div>
                    
                    {/* Coureur */}
                    <div
                      className={`absolute top-1 left-1 w-6 h-6 ${racer.color} rounded-full transition-all duration-500 flex items-center justify-center text-white text-xs font-bold`}
                      style={{
                        transform: `translateX(${(racer.position / raceTrackLength) * (100 - 8)}%)`,
                      }}
                    >
                      {racer.id}
                    </div>
                  </div>
                  
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {language === 'en' ? 'Position:' : 'Position :'} {racer.position}/{raceTrackLength}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Résultats */}
          {gamePhase === 'results' && showPredictionResult && (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                {language === 'en' ? 'Race Results' : 'Résultats de la course'}
              </h3>
              
              <div className={`p-4 rounded-lg mb-4 ${
                predictionCorrect
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
              }`}>
                <p className="font-medium">
                  {predictionCorrect
                    ? (language === 'en' ? '🎉 Correct prediction!' : '🎉 Prédiction correcte !')
                    : (language === 'en' ? '❌ Incorrect prediction' : '❌ Prédiction incorrecte')}
                </p>
                <p className="text-sm mt-1">
                  {language === 'en' 
                    ? `Winner: ${racers.find(r => r.id === raceHistory[raceHistory.length - 1]?.winner)?.name}`
                    : `Gagnant : ${racers.find(r => r.id === raceHistory[raceHistory.length - 1]?.winner)?.name}`}
                </p>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'en' ? 'Score:' : 'Score :'} {score}/{raceCount}
                </div>
                
                <button
                  onClick={nextRace}
                  className="px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600"
                >
                  {language === 'en' ? 'Next Race' : 'Course suivante'}
                </button>
              </div>
            </div>
          )}

          {/* Statistiques */}
          {raceHistory.length > 0 && (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
                {language === 'en' ? 'Race Statistics' : 'Statistiques des courses'}
              </h3>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {racers.map((racer) => (
                  <div key={racer.id} className="text-center">
                    <div className={`w-8 h-8 ${racer.color} rounded-full mx-auto mb-2`}></div>
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {racer.name}
                    </div>
                    <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                      {racer.wins}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {raceHistory.length > 0 ? Math.round((racer.wins / raceHistory.length) * 100) : 0}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProbabilityRaceGame;